import React, { useState, useEffect } from 'react';
import {
  Users, Calendar, Activity,
  ArrowUpRight, CheckCircle2, Zap, Copy, Loader2, XCircle, RefreshCw, PlayCircle, Link2, Check,
  DollarSign, Cake, BellRing, Tag
} from 'lucide-react';
import { auth } from '../config/firebase';
import { dashboardService, financeiroService, clienteService, lembreteService, servicoService, usuarioService } from '../services/api';

const FRONTEND_URL = 'https://www.agendasincro.com.br';

const formatarPreco = (valor) => {
  if (valor === null || valor === undefined) return 'R$ 0,00';
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const hojeInput = new Date().toLocaleDateString('en-CA');
const primeiroDiaMesInput = (() => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
})();

const ResumoDashboard = ({ setActiveItem, temAcesso, slug }) => {
  const isPremiumPlus = temAcesso('visao_geral_completa');
  const [dashboardData, setDashboardData] = useState(null);
  const [premiumData, setPremiumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [hasServicos, setHasServicos] = useState(true);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const [response, resServicos, resOnboarding] = await Promise.all([
          dashboardService.obterResumo(user.uid),
          servicoService.listar(user.uid).catch(() => ({ data: [] })),
          usuarioService.verificarOnboarding(user.uid).catch(() => ({ data: { passos: { google: false } } }))
        ]);
        setDashboardData({
          ...response.data,
          googleConectado: resOnboarding.data?.passos?.google || false
        });
        setHasServicos(resServicos.data && resServicos.data.length > 0);

        // Resumo extra (receita, aniversariantes, lembretes) e a Visão Geral "Completa" só
        // existem a partir do Premium — Comum vê a mesma Visão Geral Básica do Grátis.
        if (isPremiumPlus) {
          const token = await user.getIdToken();
          const [resFinanceiro, resClientes, resLembretes] = await Promise.all([
            financeiroService.obterResumo(user.uid, primeiroDiaMesInput, hojeInput).catch(() => ({ data: { receitaTotal: 0 } })),
            clienteService.listar(token).catch(() => ({ data: [] })),
            lembreteService.listar(token).catch(() => ({ data: [] })),
          ]);

          const mesAtual = new Date().getMonth() + 1;
          const aniversariantesDoMes = (resClientes.data || []).filter(c => {
            if (!c.data_nascimento) return false;
            const mes = parseInt(String(c.data_nascimento).slice(5, 7), 10);
            return mes === mesAtual;
          });
          const lembretesPendentes = (resLembretes.data || []).filter(l => l.status === 'PENDENTE');

          setPremiumData({
            receitaMes: resFinanceiro.data.receitaTotal,
            aniversariantesDoMes,
            lembretesPendentes,
          });
        }
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, [isPremiumPlus]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] w-full gap-4">
        <Loader2 className="animate-spin text-[#1883FF]" size={40} />
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sincronizando dados...</p>
      </div>
    );
  }

  const agendaPublica = slug
    ? { label: 'Ativa', textColor: 'text-[#10b981]', bgColor: 'bg-[#10b981]/15', icon: <CheckCircle2 size={22} /> }
    : { label: 'Configurar', textColor: 'text-[#f59e0b]', bgColor: 'bg-[#f59e0b]/15', icon: <Link2 size={22} /> };

  const wa = (() => {
    const status = dashboardData?.statusWhatsapp || 'DISCONNECTED';
    if (status === 'CONNECTED') return { label: 'Operacional', textColor: 'text-[#10b981]', bgColor: 'bg-[#10b981]/15', icon: <CheckCircle2 size={22} /> };
    if (status === 'CONNECTING') return { label: 'Pareando...', textColor: 'text-[#f59e0b]', bgColor: 'bg-[#f59e0b]/15', icon: <RefreshCw size={22} className="animate-spin" /> };
    return { label: 'Desconectada', textColor: 'text-[#ef4444]', bgColor: 'bg-[#ef4444]/15', icon: <XCircle size={22} /> };
  })();

  // A Agenda Pública funciona em qualquer plano, então o status dela aparece pra todo mundo.
  // A Instância WhatsApp e o resumo financeiro/marketing só existem a partir do Premium.
  const stats = [
    { label: 'Atendimentos Hoje', value: dashboardData?.consultasHoje ? String(dashboardData.consultasHoje).padStart(2, '0') : '00', icon: <Calendar size={22} />, textColor: 'text-[#1883FF]', bgColor: 'bg-[#1883FF]/15' },
    { label: 'Agenda Pública', value: agendaPublica.label, icon: agendaPublica.icon, textColor: agendaPublica.textColor, bgColor: agendaPublica.bgColor, isStatus: true },
    ...(isPremiumPlus ? [{ label: 'Instância WhatsApp', value: wa.label, icon: wa.icon, textColor: wa.textColor, bgColor: wa.bgColor, isStatus: true }] : []),
    { label: 'Novos Pacientes (30d)', value: dashboardData?.novosClientes ? String(dashboardData.novosClientes).padStart(2, '0') : '00', icon: <Users size={22} />, textColor: 'text-[#6D28D9]', bgColor: 'bg-[#6D28D9]/15' },
    // Atendimento concluído sem lembrete: o worker automático só avisa quem tem consulta
    // "agendado" — depois que o profissional marca como concluído, nada mais vai lembrar o
    // paciente, então esse card avisa quando isso ficou pra trás.
    (() => {
      const semLembrete = dashboardData?.consultasConcluidasSemLembrete || 0;
      return {
        label: 'Sem Lembrete Enviado',
        value: String(semLembrete).padStart(2, '0'),
        icon: <BellRing size={22} />,
        textColor: semLembrete > 0 ? 'text-red-600' : 'text-emerald-600',
        bgColor: semLembrete > 0 ? 'bg-red-500/15' : 'bg-emerald-500/15',
        onClick: () => setActiveItem('meusAgendamentos'),
      };
    })(),
    ...(isPremiumPlus && premiumData ? [
      { label: 'Receita do Mês', value: formatarPreco(premiumData.receitaMes), icon: <DollarSign size={22} />, textColor: 'text-emerald-600', bgColor: 'bg-emerald-500/15', onClick: () => setActiveItem('financeiro') },
      { label: 'Aniversariantes do Mês', value: String(premiumData.aniversariantesDoMes.length).padStart(2, '0'), icon: <Cake size={22} />, textColor: 'text-pink-600', bgColor: 'bg-pink-500/15', onClick: () => setActiveItem('marketing') },
      { label: 'Lembretes Pendentes', value: String(premiumData.lembretesPendentes.length).padStart(2, '0'), icon: <BellRing size={22} />, textColor: 'text-amber-600', bgColor: 'bg-amber-500/15', onClick: () => setActiveItem('marketing') },
    ] : []),
  ];

  const copiarLinkAgenda = async () => {
    if (!slug) return setActiveItem('minhaAgenda');
    try {
      await navigator.clipboard.writeText(`${FRONTEND_URL}/${slug}`);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 2500);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
    }
  };

  return (
    <div className="flex flex-col gap-8 animate-fade-in-up">

      {/* Banner 1: Catálogo de Serviços */}
      {!hasServicos && (
        <div className="bg-gradient-to-br from-[#F5F3FF] to-[#E0E7FF] p-6 rounded-[24px] mb-4 border border-purple-100 flex items-center justify-between gap-5 flex-wrap">
          <div className="flex gap-4 items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] rounded-full flex items-center justify-center text-white shrink-0">
              <PlayCircle size={24} />
            </div>
            <div>
              <h4 className="margin-0 text-sm font-black text-[#1e293b] uppercase">
                Quase lá, {auth.currentUser?.displayName?.split(' ')[0] || 'Profissional'}!
              </h4>
              <p className="m-0 mt-1 text-xs text-[#6D28D9] font-bold">
                Cadastre seus serviços para que seus clientes possam agendar.
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveItem('servicos')}
            className="px-5 py-2.5 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-xl text-[10px] font-black cursor-pointer uppercase transition-opacity hover:opacity-90"
          >
            Configurar Serviços
          </button>
        </div>
      )}

      {/* Banner 2: Integrações (WhatsApp e Google Agenda) */}
      {((isPremiumPlus && dashboardData?.statusWhatsapp !== 'CONNECTED') || !dashboardData?.googleConectado) && (
        <div className="bg-gradient-to-br from-[#F5F3FF] to-[#E0E7FF] p-6 rounded-[24px] mb-4 border border-purple-100 flex items-center justify-between gap-5 flex-wrap">
          <div className="flex gap-4 items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] rounded-full flex items-center justify-center text-white shrink-0">
              <PlayCircle size={24} />
            </div>
            <div>
              <h4 className="margin-0 text-sm font-black text-[#1e293b] uppercase">
                Vamos para a segunda etapa, {auth.currentUser?.displayName?.split(' ')[0] || 'Profissional'}!
              </h4>
              <p className="m-0 mt-1 text-xs text-[#6D28D9] font-bold">
                {isPremiumPlus
                  ? (dashboardData?.statusWhatsapp !== 'CONNECTED' && !dashboardData?.googleConectado
                    ? 'Conecte seu WhatsApp para ativar a IA.'
                    : dashboardData?.statusWhatsapp !== 'CONNECTED'
                      ? 'Conecte seu WhatsApp para ativar a IA.'
                      : 'Conecte sua Google Agenda para receber agendamentos pelo seu link público.')
                  : 'Conecte sua Google Agenda para receber agendamentos pelo seu link público.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveItem('automacao')}
            className="px-5 py-2.5 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-xl text-[10px] font-black cursor-pointer uppercase transition-opacity hover:opacity-90"
          >
            Conectar Integrações
          </button>
        </div>
      )}

      {/* Banner 3: Tudo configurado com Sucesso */}
      {hasServicos && (!isPremiumPlus || dashboardData?.statusWhatsapp === 'CONNECTED') && dashboardData?.googleConectado && (
        <div className="bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7] p-6 rounded-[24px] mb-4 border border-emerald-100 flex items-center justify-between gap-5 flex-wrap">
          <div className="flex gap-4 items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full flex items-center justify-center text-white shrink-0">
              <CheckCircle2 size={24} className="text-white" />
            </div>
            <div>
              <h4 className="margin-0 text-sm font-black text-[#14532d] uppercase">
                Tudo pronto, {auth.currentUser?.displayName?.split(' ')[0] || 'Profissional'}!
              </h4>
              <p className="m-0 mt-1 text-xs text-[#15803d] font-bold">
                Sua agenda e integrações estão ativas. Seu assistente de IA está pronto para receber agendamentos!
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveItem('meusAgendamentos')}
            className="px-5 py-2.5 bg-gradient-to-r from-[#10B981] to-[#059669] text-white border-none rounded-xl text-[10px] font-black cursor-pointer uppercase transition-opacity hover:opacity-90"
          >
            Ver Agendamentos
          </button>
        </div>
      )}

      {/* Stats Section / Grid Adaptável */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${isPremiumPlus ? 'xl:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        {stats.map((item, idx) => (
          <div
            key={idx}
            onClick={item.onClick}
            className={`bg-[#F8F7FF] p-6 rounded-[2rem] border border-purple-50 flex items-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_20px_40px_rgba(109,40,217,0.05)] hover:border-[#6D28D9]/20 ${item.onClick ? 'cursor-pointer' : ''}`}
          >
            <div className={`w-14 h-14 rounded-[18px] flex items-center justify-center shrink-0 ${item.bgColor} ${item.textColor}`}>{item.icon}</div>
            <div className="min-w-0">
              <p className="text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-1 break-words">{item.label}</p>
              <p className={`text-2xl font-black tracking-tight m-0 break-words ${item.isStatus ? item.textColor : 'text-[#1e293b]'}`}>{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Layout de Duas Colunas Responsivo */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-6">

        {/* Painel Esquerdo: Linha do Tempo */}
        <div className="bg-white p-8 rounded-[2.5rem] border border-purple-50">
          <h3 className="text-sm font-black text-[#1e293b] uppercase tracking-wider mb-7 flex items-center gap-2.5 italic">
            <Activity size={18} className="text-[#6D28D9]" /> Atendimentos Programados
          </h3>

          <div className="relative">
            {!dashboardData?.proximasConsultas || dashboardData.proximasConsultas.length === 0 ? (
              <p className="text-sm font-semibold text-slate-400 italic py-4">Nenhum atendimento para as próximas horas.</p>
            ) : (
              dashboardData.proximasConsultas.map((consulta, index) => (
                <div className="flex gap-5 pb-7 last:pb-2" key={index}>
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] border-4 border-white shadow-[0_0_0_1px_#e2e8f0] shrink-0"></div>
                    <div className="w-[2px] flex-1 bg-slate-100 mt-1"></div>
                  </div>
                  <div>
                    <p className="text-[11px] font-black text-[#1883FF] mb-1 uppercase tracking-wider">{consulta.hora} • AGENDAMENTO</p>
                    <p className="text-sm font-medium text-[#475569] m-0"><strong>{consulta.nome}</strong> via WhatsApp.</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Painel Direito: Ações Rápidas */}
        <div className="flex flex-col gap-6">
          <div className="bg-white p-7 rounded-[2.5rem] border border-purple-50">
            <h3 className="text-sm font-black text-[#1e293b] uppercase tracking-wider mb-7 flex items-center gap-2.5 italic">Ações Rápidas</h3>

            <button className="w-full p-4 bg-[#F8F7FF] border border-purple-50 rounded-[18px] text-[#475569] text-[11px] font-black uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 mb-3 hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:text-white hover:border-[#6D28D9] hover:-translate-y-0.5" onClick={copiarLinkAgenda}>
              {linkCopiado ? <Check size={16} /> : <Copy size={16} />} {linkCopiado ? 'Copiado!' : 'Copiar Link da Agenda'}
            </button>
            <button className="w-full p-4 bg-[#F8F7FF] border border-purple-50 rounded-[18px] text-[#475569] text-[11px] font-black uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 mb-3 hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:text-white hover:border-[#6D28D9] hover:-translate-y-0.5" onClick={() => setActiveItem('help')}>
              <Zap size={16} /> Ver Tutorial
            </button>
            <button className="w-full p-4 bg-[#F8F7FF] border border-purple-50 rounded-[18px] text-[#475569] text-[11px] font-black uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 mb-3 hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:text-white hover:border-[#6D28D9] hover:-translate-y-0.5" onClick={() => setActiveItem('automacao')}>
              <ArrowUpRight size={16} /> Configurar Automação
            </button>
            <button className="w-full p-4 bg-[#F8F7FF] border border-purple-50 rounded-[18px] text-[#475569] text-[11px] font-black uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 mb-3 hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:text-white hover:border-[#6D28D9] hover:-translate-y-0.5" onClick={() => setActiveItem('servicos')}>
              <Tag size={16} /> Cadastrar Serviços
            </button>

            {/* Card Upgrade Interno Sincro COM MASCOTE */}
            <div className="mt-5 bg-gradient-to-br from-[#1883FF] to-[#6D28D9] p-6 sm:p-7 rounded-[2rem] text-white shadow-lg relative">

              {/* Container do Texto blindado com padding-right para dar espaço e não cobrir o texto */}
              <div className="relative z-10 pr-24 sm:pr-28">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/80 mb-3">
                  <Link2 size={12} /> Sua Agenda Pública
                </div>
                <p className="text-[11px] sm:text-xs font-bold leading-snug mb-5 text-slate-100 italic">
                  {slug
                    ? 'Compartilhe seu link com os pacientes e receba agendamentos direto, sem robô de WhatsApp.'
                    : 'Configure seu link em Perfil para começar a receber agendamentos.'}
                </p>
                <button
                  onClick={copiarLinkAgenda}
                  className="flex items-center gap-2 text-[10px] font-black text-white uppercase bg-white/15 border-none rounded-lg px-3 py-2 cursor-pointer hover:bg-white/25 transition-colors"
                >
                  {linkCopiado ? <Check size={12} /> : <Copy size={12} />} {linkCopiado ? 'Copiado!' : slug ? 'Copiar Link' : 'Configurar Link'}
                </button>
              </div>

              {/* Imagem do Mascote perfeitamente alinhada na base e direita (sem cortes de overflow) */}
              <img
                src="/8.png"
                alt="Mascote Sincro IA"
                className="absolute right-0 bottom-0 w-28 sm:w-32 object-contain drop-shadow-2xl"
              />

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ResumoDashboard;
